import React, { useCallback, useEffect, useRef, useState } from 'react';
import SplashScreen from './SplashScreen';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import { FaKey, FaLock, FaUser, FaUserCircle } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as faceapi from '@vladmandic/face-api';
import { API_BASE } from '../api';

const FACE_MODEL_URL = 'https://vladmandic.github.io/face-api/model';
const FACE_MATCH_THRESHOLD = 0.58;
const FACE_FAST_ACCEPT_DISTANCE = 0.36;
const FACE_MIN_CONFIDENCE_GAP = 0.045;
const FACE_AUTO_SCAN_INTERVAL_MS = 1300;
const FACE_MEMBERS_INSTITUICAO_ID = Number.parseInt(process.env.REACT_APP_FACE_INSTITUICAO_ID || '0', 10);
const FACE_SETUP_ERROR_TOAST_ID = 'face-setup-error';
const FACE_MATCH_ERROR_TOAST_ID = 'face-match-error';
const FACE_MAX_AUTO_LAZY_LOAD_MOBILE = 8;
const FACE_MAX_AUTO_LAZY_LOAD_DESKTOP = 14;

const extractDigits = (value) => String(value || '').replace(/\D/g, '');
const formatCpf = (value) => {
  const digits = extractDigits(value);
  if (digits.length !== 11) {
    return digits;
  }
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const getMemberDescriptorKey = (member) => String(member?.id_pessoa || member?.foto || '').trim();

const hasConfidentDistance = (bestDistance, secondBestDistance) => {
  if (!Number.isFinite(bestDistance)) {
    return false;
  }

  const confidenceGap = Number.isFinite(secondBestDistance)
    ? secondBestDistance - bestDistance
    : Number.POSITIVE_INFINITY;

  return bestDistance <= FACE_MATCH_THRESHOLD && (
    bestDistance <= 0.53 ||
    confidenceGap >= FACE_MIN_CONFIDENCE_GAP
  );
};

const Login = ({ setUser }) => {
  const sharedInputStyles = {
    bg: 'white',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    color: '#0b1725',
    fontSize: { base: '16px', md: '18px' },
    letterSpacing: '0.01em',
    fontWeight: '600',
    borderRadius: '16px',
    boxShadow: '0 16px 35px rgba(10, 31, 75, 0.4)',
    px: 4,
    py: 4,
    transition: 'all 0.2s ease',
    _hover: { borderColor: '#ffffff' },
    _placeholder: {
      color: 'rgba(11, 23, 37, 0.65)',
      fontWeight: '500',
      fontSize: { base: '14px', md: '15px' },
      letterSpacing: '0.005em'
    },
    _focusVisible: {
      borderColor: '#ffffff',
      boxShadow: '0 0 0 3px rgba(255,255,255,0.35)'
    },
    sx: {
      '@keyframes pulsePlaceholder': {
        '0%': { opacity: 0.45, transform: 'translateY(0)' },
        '50%': { opacity: 0.85, transform: 'translateY(-1px)' },
        '100%': { opacity: 0.45, transform: 'translateY(0)' }
      },
      '&::placeholder': {
        animation: 'pulsePlaceholder 2.2s ease-in-out infinite'
      }
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [showSplash, setShowSplash] = useState(false);
  const [cim, setCim] = useState('');
  const [cpf, setCpf] = useState('');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [pendingUser, setPendingUser] = useState(null);

  const [faceStatus, setFaceStatus] = useState('');
  const [faceError, setFaceError] = useState('');
  const [isFaceSetupLoading, setIsFaceSetupLoading] = useState(false);
  const [isFaceMatching, setIsFaceMatching] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [facePin, setFacePin] = useState('');
  const [facePinLoading, setFacePinLoading] = useState(false);
  const [faceTargetMember, setFaceTargetMember] = useState(null);

  const toast = useToast();
  const navigate = useNavigate();
  const firstAccessModal = useDisclosure();
  const passwordModal = useDisclosure();
  const faceModal = useDisclosure();

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const membersCacheRef = useRef([]);
  const descriptorCacheRef = useRef(new Map());
  const descriptorPromiseCacheRef = useRef(new Map());
  const modelsLoadedRef = useRef(false);
  const faceInitRef = useRef(false);
  const faceSetupPromiseRef = useRef(null);
  const initializeFaceAuthRef = useRef(null);
  const autoScanIntervalRef = useRef(null);
  const autoScanCooldownUntilRef = useRef(0);
  const faceMatchingRef = useRef(false);
  const warmupStartedRef = useRef(false);
  const warmupPromiseRef = useRef(null);

  const handleCimChange = (e) => {
    const value = extractDigits(e.target.value);
    setCim(value);
  };

  const handleCpfChange = (e) => {
    const value = extractDigits(e.target.value);
    if (value.length <= 11) {
      const formattedCpf = value
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      setCpf(formattedCpf);
    }
  };

  const resetFaceRuntimeState = useCallback(() => {
    warmupStartedRef.current = false;
    warmupPromiseRef.current = null;
    descriptorPromiseCacheRef.current.clear();
    faceMatchingRef.current = false;
    autoScanCooldownUntilRef.current = 0;
  }, []);

  const resetPasswordState = () => {
    setAttempts(0);
    setPassword('');
    setPasswordError('');
  };

  const finishLoginWithUser = useCallback((userData) => {
    if (!userData) {
      return;
    }
    sessionStorage.setItem('user', JSON.stringify(userData));
    sessionStorage.removeItem('pending_access');
    setUser(userData);
    setShowSplash(true);
    setTimeout(() => {
      navigate('/');
    }, 4000);
  }, [navigate, setUser]);

  const performLegacyLogin = useCallback(async (inputCim, inputCpf) => {
    const rawCim = String(inputCim || '').trim();
    const rawCpf = String(inputCpf || '').trim();
    const cimDigits = extractDigits(rawCim);
    const cpfDigits = extractDigits(rawCpf);
    const cpfFormatted = formatCpf(cpfDigits);

    const candidatePairs = [
      { cim: cimDigits, cpf: cpfDigits },
      { cim: rawCim, cpf: rawCpf },
      { cim: cimDigits, cpf: cpfFormatted },
      { cim: rawCim, cpf: cpfDigits },
      { cim: cimDigits, cpf: rawCpf }
    ]
      .map((item) => ({
        cim: String(item.cim || '').trim(),
        cpf: String(item.cpf || '').trim()
      }))
      .filter((item) => item.cim && item.cpf)
      .filter((item, index, array) =>
        array.findIndex((other) => other.cim === item.cim && other.cpf === item.cpf) === index
      );

    for (let index = 0; index < candidatePairs.length; index += 1) {
      try {
        const response = await axios.post(`${API_BASE}/Login`, candidatePairs[index]);
        const users = response.data?.result?.[0] || [];
        if (users.length > 0) {
          return users[0];
        }
      } catch (error) {
        // tenta o proximo formato
      }
    }

    return null;
  }, []);

  const performFacialLogin = useCallback(async (member) => {
    const memberId = Number.parseInt(member?.id_pessoa || 0, 10);
    const cimDigits = extractDigits(member?.cim || '');
    const cpfDigits = extractDigits(member?.cpf || '');
    const cpfFormatted = formatCpf(cpfDigits);

    try {
      const response = await axios.post(`${API_BASE}/auth/facial-login`, {
        id_pessoa: Number.isInteger(memberId) && memberId > 0 ? memberId : null,
        cim: cimDigits,
        cpf: cpfDigits
      });

      const userData = response.data?.user || response.data?.result?.[0]?.[0];
      if (userData) {
        return userData;
      }
    } catch (error) {
      if (error?.response?.status && error.response.status !== 404) {
        // segue para fallback legacy
      }
    }

    if (cimDigits && cpfDigits) {
      const legacyUser = await performLegacyLogin(cimDigits, cpfDigits);
      if (legacyUser) {
        return legacyUser;
      }
    }

    const checkAccessCandidates = [
      { cim: cimDigits, cpf: cpfDigits },
      { cim: cimDigits, cpf: cpfFormatted },
      { cim: String(member?.cim || ''), cpf: String(member?.cpf || '') }
    ]
      .map((item) => ({
        cim: String(item.cim || '').trim(),
        cpf: String(item.cpf || '').trim()
      }))
      .filter((item) => item.cim && item.cpf)
      .filter((item, index, array) =>
        array.findIndex((other) => other.cim === item.cim && other.cpf === item.cpf) === index
      );

    for (let index = 0; index < checkAccessCandidates.length; index += 1) {
      try {
        const response = await axios.post(`${API_BASE}/auth/check-first-access`, checkAccessCandidates[index]);
        if (response.data?.userExists && response.data?.user) {
          return response.data.user;
        }
      } catch (error) {
        // ignora e tenta proximo fallback
      }
    }

    if (Number.isInteger(memberId) && memberId > 0) {
      return {
        id_pessoa: memberId,
        nome: member?.nome || '',
        cim: member?.cim || cimDigits,
        cpf: member?.cpf || cpfDigits,
        foto: member?.foto || null
      };
    }

    return null;
  }, [performLegacyLogin]);

  const handleTooManyAttempts = (message) => {
    toast({
      title: 'Senha incorreta',
      description: message,
      status: 'error',
      duration: 5000,
      isClosable: true
    });
    resetPasswordState();
    setCim('');
    setCpf('');
    setError('');
    passwordModal.onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE}/auth/check-first-access`, { cim, cpf });
      const data = response.data || {};

      if (!data.userExists) {
        setError('CIM ou CPF incorreto');
        toast({
          title: 'Erro no login',
          description: data.message || 'CIM ou CPF incorreto. Tente novamente.',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
        setIsLoading(false);
        return;
      }

      const accessData = {
        userId: data.userId,
        cim,
        cpf,
        nome: data.user?.nome
      };
      setPendingUser(accessData);

      if (data.firstAccess) {
        sessionStorage.setItem('pending_access', JSON.stringify(accessData));
        firstAccessModal.onOpen();
      } else {
        resetPasswordState();
        passwordModal.onOpen();
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor. Tente novamente mais tarde.');
      toast({
        title: 'Erro no servidor',
        description: 'Nao foi possivel conectar ao servidor.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError('Informe a senha.');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');
    try {
      const loginResp = await axios.post(`${API_BASE}/auth/login`, {
        cim: pendingUser?.cim || cim,
        cpf: pendingUser?.cpf || cpf,
        password
      });

      const userData = loginResp.data?.user || loginResp.data?.userData || loginResp.data?.result?.[0]?.[0];
      if (!userData) {
        throw new Error('Usuario nao retornado.');
      }

      passwordModal.onClose();
      finishLoginWithUser(userData);
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.error || 'Senha incorreta. Tente novamente.';

      if (status === 429) {
        handleTooManyAttempts(message);
        setPasswordLoading(false);
        return;
      }

      if (status && status >= 500) {
        toast({
          title: 'Erro no servidor',
          description: message,
          status: 'error',
          duration: 5000,
          isClosable: true
        });
        setPasswordLoading(false);
        return;
      }

      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);
      if (nextAttempts >= 3) {
        handleTooManyAttempts('Voce errou a senha 3 vezes. Tente novamente.');
      } else {
        setPasswordError(message);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleOpenFaceModal = () => {
    setFacePin('');
    setFacePinLoading(false);
    setFaceTargetMember(null);
    setFaceError('');
    setFaceStatus('Informe seu CIM para iniciar o reconhecimento facial.');
    faceModal.onOpen();
  };

  const stopCamera = useCallback(() => {
    if (autoScanIntervalRef.current) {
      clearInterval(autoScanIntervalRef.current);
      autoScanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    faceMatchingRef.current = false;
    setIsCameraReady(false);
  }, []);

  const handleFacePinSubmit = useCallback(async () => {
    const pinDigits = extractDigits(facePin);
    if (!pinDigits) {
      setFaceError('Informe o CIM.');
      return;
    }

    setFacePinLoading(true);
    setFaceError('');
    setFaceStatus('Validando CIM...');
    try {
      const response = await axios.post(`${API_BASE}/auth/facial-pin`, { cim: pinDigits });
      const member = response.data?.member || response.data?.user || null;
      if (!member || !member.id_pessoa) {
        throw new Error('CIM invalido.');
      }

      stopCamera();
      resetFaceRuntimeState();
      faceInitRef.current = false;
      faceSetupPromiseRef.current = null;

      setFaceTargetMember(member);
      setFaceStatus(`CIM validado para ${member.nome || 'membro'}. Inicializando camera...`);
    } catch (pinError) {
      setFaceTargetMember(null);
      setFaceStatus('');
      setFaceError(pinError?.response?.data?.error || pinError.message || 'CIM invalido.');
    } finally {
      setFacePinLoading(false);
    }
  }, [facePin, resetFaceRuntimeState, stopCamera]);

  const ensureFaceModelsLoaded = useCallback(async () => {
    if (faceapi?.tf) {
      const currentBackend = typeof faceapi.tf.getBackend === 'function'
        ? faceapi.tf.getBackend()
        : '';

      if (!currentBackend || currentBackend === 'wasm') {
        let backendReady = false;

        try {
          await faceapi.tf.setBackend('webgl');
          await faceapi.tf.ready();
          backendReady = true;
        } catch (webglError) {
          try {
            await faceapi.tf.setBackend('cpu');
            await faceapi.tf.ready();
            backendReady = true;
          } catch (cpuError) {
            throw new Error('Nao foi possivel inicializar o reconhecimento facial neste navegador.');
          }
        }

        if (!backendReady) {
          throw new Error('Nao foi possivel inicializar o reconhecimento facial neste navegador.');
        }
      } else {
        await faceapi.tf.ready();
      }
    }

    if (modelsLoadedRef.current) {
      return;
    }
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(FACE_MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(FACE_MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(FACE_MODEL_URL)
    ]);
    modelsLoadedRef.current = true;
  }, []);

  const buildFaceDetectorOptions = useCallback((profile = 'live') => {
    const useMobilePreset = window.innerWidth <= 768;
    const isPhotoProfile = profile === 'photo';
    return new faceapi.TinyFaceDetectorOptions({
      inputSize: useMobilePreset
        ? (isPhotoProfile ? 160 : 192)
        : (isPhotoProfile ? 192 : 256),
      scoreThreshold: useMobilePreset
        ? (isPhotoProfile ? 0.34 : 0.38)
        : (isPhotoProfile ? 0.38 : 0.42)
    });
  }, []);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Seu navegador nao suporta camera para reconhecimento facial.');
    }

    const videoElement = videoRef.current;
    if (!videoElement) {
      throw new Error('Nao foi possivel iniciar o video da camera.');
    }

    videoElement.muted = true;
    videoElement.autoplay = true;
    videoElement.playsInline = true;
    videoElement.setAttribute('muted', 'true');
    videoElement.setAttribute('autoplay', 'true');
    videoElement.setAttribute('playsinline', 'true');
    videoElement.setAttribute('webkit-playsinline', 'true');

    const streamInactive =
      streamRef.current &&
      streamRef.current.getVideoTracks().every((track) => track.readyState !== 'live');

    if (streamInactive) {
      streamRef.current = null;
    }

    if (!streamRef.current) {
      const cameraProfiles = [
        {
          video: {
            facingMode: { ideal: 'user' },
            width: { ideal: 640 },
            height: { ideal: 480 }
          },
          audio: false
        },
        {
          video: {
            facingMode: 'user'
          },
          audio: false
        },
        {
          video: true,
          audio: false
        }
      ];

      let lastCameraError = null;
      for (let index = 0; index < cameraProfiles.length; index += 1) {
        try {
          // Testa perfis de camera para cobrir Safari iOS e Android com drivers diferentes.
          const stream = await navigator.mediaDevices.getUserMedia(cameraProfiles[index]);
          streamRef.current = stream;
          lastCameraError = null;
          break;
        } catch (cameraError) {
          lastCameraError = cameraError;
        }
      }

      if (!streamRef.current) {
        throw lastCameraError || new Error('Nao foi possivel acessar a camera.');
      }
    }

    if (videoElement.srcObject !== streamRef.current) {
      videoElement.srcObject = streamRef.current;
    }

    await new Promise((resolve) => {
      if (videoElement.readyState >= 1) {
        resolve();
        return;
      }
      const handler = () => {
        videoElement.removeEventListener('loadedmetadata', handler);
        resolve();
      };
      videoElement.addEventListener('loadedmetadata', handler);
    });

    let lastError = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        await videoElement.play();
        lastError = null;
        break;
      } catch (error) {
        lastError = error;
        const lowerMessage = String(error?.message || '').toLowerCase();
        const retryable =
          error?.name === 'AbortError' ||
          lowerMessage.includes('interrupted by a new load request');

        if (!retryable) {
          throw error;
        }

        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }

    if (lastError) {
      throw lastError;
    }

    setIsCameraReady(true);
  }, []);

  const loadMembersForFaceAuth = useCallback(async () => {
    if (membersCacheRef.current.length > 0) {
      return membersCacheRef.current;
    }

    const instituicaoId = Number.isInteger(FACE_MEMBERS_INSTITUICAO_ID) && FACE_MEMBERS_INSTITUICAO_ID > 0
      ? FACE_MEMBERS_INSTITUICAO_ID
      : 0;
    const response = await axios.get(`${API_BASE}/getMembros?id_pessoa=0&id_insituicao=${instituicaoId}`);
    const members = response.data?.result?.[0] || [];
    const validMembers = members.filter((member) => {
      const hasPhoto = Boolean(member?.foto) || Number.parseInt(member?.id_pessoa || 0, 10) > 0;
      const hasIdentity = extractDigits(member?.cim).length > 0 && extractDigits(member?.cpf).length > 0;
      return Boolean(hasPhoto && hasIdentity);
    }).sort((a, b) => {
      const aHasExplicitPhoto = Boolean(a?.foto);
      const bHasExplicitPhoto = Boolean(b?.foto);
      return Number(bHasExplicitPhoto) - Number(aHasExplicitPhoto);
    });

    membersCacheRef.current = validMembers;
    return validMembers;
  }, []);

  const loadImageElement = useCallback((imageUrl, timeoutMs = 1400) => {
    return new Promise((resolve, reject) => {
      const image = new window.Image();
      let timeoutId = null;
      const clearHandlers = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        image.onload = null;
        image.onerror = null;
      };
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        clearHandlers();
        resolve(image);
      };
      image.onerror = () => {
        clearHandlers();
        reject(new Error('Nao foi possivel carregar foto para comparacao.'));
      };
      timeoutId = setTimeout(() => {
        clearHandlers();
        reject(new Error('Tempo limite ao carregar foto para comparacao.'));
      }, timeoutMs);
      image.src = imageUrl;
    });
  }, []);

  const buildMemberPhotoCandidates = useCallback((member) => {
    const candidates = [];
    const registerCandidate = (value) => {
      const raw = String(value || '').trim();
      if (!raw) {
        return;
      }

      if (raw.includes('.')) {
        candidates.push(`${API_BASE}/Fotos/${raw}`);
        return;
      }

      candidates.push(`${API_BASE}/Fotos/${raw}.png`);
      candidates.push(`${API_BASE}/Fotos/${raw}.jpg`);
      candidates.push(`${API_BASE}/Fotos/${raw}.jpeg`);
      candidates.push(`${API_BASE}/Fotos/${raw}`);
    };

    registerCandidate(member?.foto);
    if (!member?.foto) {
      registerCandidate(member?.id_pessoa);
    }

    return Array.from(new Set(candidates));
  }, []);

  const getMemberDescriptor = useCallback(
    async (member) => {
      const memberId = getMemberDescriptorKey(member);
      if (!memberId) {
        return null;
      }

      if (descriptorCacheRef.current.has(memberId)) {
        return descriptorCacheRef.current.get(memberId);
      }

      if (descriptorPromiseCacheRef.current.has(memberId)) {
        return descriptorPromiseCacheRef.current.get(memberId);
      }

      const resolver = (async () => {
        const candidates = buildMemberPhotoCandidates(member);
        for (let index = 0; index < candidates.length; index += 1) {
          try {
            const image = await loadImageElement(candidates[index]);
            const detection = await faceapi
              .detectSingleFace(image, buildFaceDetectorOptions('photo'))
              .withFaceLandmarks()
              .withFaceDescriptor();

            if (!detection) {
              continue;
            }

            descriptorCacheRef.current.set(memberId, detection.descriptor);
            return detection.descriptor;
          } catch (descriptorError) {
            // tenta proximo candidato
          }
        }

        descriptorCacheRef.current.set(memberId, null);
        return null;
      })();

      descriptorPromiseCacheRef.current.set(memberId, resolver);
      try {
        return await resolver;
      } finally {
        descriptorPromiseCacheRef.current.delete(memberId);
      }
    },
    [buildFaceDetectorOptions, buildMemberPhotoCandidates, loadImageElement]
  );

  const getCachedMemberDescriptor = useCallback((member) => {
    const memberId = getMemberDescriptorKey(member);
    if (!memberId) {
      return undefined;
    }
    if (!descriptorCacheRef.current.has(memberId)) {
      return undefined;
    }
    return descriptorCacheRef.current.get(memberId);
  }, []);

  const findBestFaceMatch = useCallback(
    async (
      liveDescriptor,
      members,
      {
        allowLazyLoad = true,
        maxMembersToLoad = Number.POSITIVE_INFINITY
      } = {}
    ) => {
      let bestDistance = Number.POSITIVE_INFINITY;
      let secondBestDistance = Number.POSITIVE_INFINITY;
      let bestMember = null;
      let comparedFaces = 0;

      const compareDescriptor = (member, descriptor) => {
        if (!descriptor) {
          return;
        }
        comparedFaces += 1;
        const distance = faceapi.euclideanDistance(liveDescriptor, descriptor);

        if (distance < bestDistance) {
          secondBestDistance = bestDistance;
          bestDistance = distance;
          bestMember = member;
        } else if (distance < secondBestDistance) {
          secondBestDistance = distance;
        }
      };

      const uncachedMembers = [];
      for (let index = 0; index < members.length; index += 1) {
        const member = members[index];
        const cachedDescriptor = getCachedMemberDescriptor(member);
        if (cachedDescriptor === undefined) {
          uncachedMembers.push(member);
          continue;
        }
        compareDescriptor(member, cachedDescriptor);
      }

      if (bestDistance <= FACE_FAST_ACCEPT_DISTANCE) {
        return { bestMember, bestDistance, secondBestDistance, comparedFaces };
      }

      if (!allowLazyLoad || uncachedMembers.length === 0 || maxMembersToLoad <= 0) {
        return { bestMember, bestDistance, secondBestDistance, comparedFaces };
      }

      const batchSize = window.innerWidth <= 768 ? 3 : 7;
      let loadedMembers = 0;

      for (let start = 0; start < uncachedMembers.length; start += batchSize) {
        if (loadedMembers >= maxMembersToLoad) {
          break;
        }

        setFaceStatus(`Comparando rosto... ${Math.min(comparedFaces, members.length)}/${members.length}`);
        const batch = uncachedMembers.slice(start, start + batchSize);
        const descriptors = await Promise.all(batch.map((member) => getMemberDescriptor(member)));

        for (let index = 0; index < batch.length; index += 1) {
          compareDescriptor(batch[index], descriptors[index]);
        }

        loadedMembers += batch.length;
        if (bestDistance <= FACE_FAST_ACCEPT_DISTANCE || hasConfidentDistance(bestDistance, secondBestDistance)) {
          break;
        }
      }

      return { bestMember, bestDistance, secondBestDistance, comparedFaces };
    },
    [getCachedMemberDescriptor, getMemberDescriptor]
  );

  const warmupFaceDescriptors = useCallback(async (members) => {
    if (warmupStartedRef.current || !Array.isArray(members) || members.length === 0) {
      return;
    }

    warmupStartedRef.current = true;
    const queue = [...members];
    const workerCount = window.innerWidth <= 768 ? 2 : 4;
    let cursor = 0;

    const runWorker = async () => {
      while (cursor < queue.length) {
        const targetIndex = cursor;
        cursor += 1;
        const member = queue[targetIndex];
        if (!member) {
          continue;
        }
        try {
          await getMemberDescriptor(member);
        } catch (error) {
          // segue o preload com os demais registros
        }
      }
    };

    warmupPromiseRef.current = Promise.all(
      Array.from({ length: workerCount }, () => runWorker())
    ).finally(() => {
      warmupPromiseRef.current = null;
    });

    await warmupPromiseRef.current;
  }, [getMemberDescriptor]);

  const initializeFaceAuth = useCallback(async (targetMember = faceTargetMember) => {
    if (!targetMember) {
      setFaceStatus('Informe seu CIM para iniciar o reconhecimento facial.');
      setFaceError('');
      return;
    }

    if (faceSetupPromiseRef.current) {
      await faceSetupPromiseRef.current;
      return;
    }

    faceSetupPromiseRef.current = (async () => {
      if (faceInitRef.current) {
        return;
      }

      faceInitRef.current = true;
      setFaceError('');
      setFaceStatus('Preparando reconhecimento facial...');
      setIsFaceSetupLoading(true);

      try {
        await ensureFaceModelsLoaded();
        setFaceStatus('Modelos carregados. Iniciando camera...');
        await startCamera();
        const targetDescriptor = await getMemberDescriptor(targetMember);
        if (!targetDescriptor) {
          throw new Error('Nao foi possivel carregar a foto do CIM informado.');
        }
        setFaceStatus(`Camera pronta para ${targetMember?.nome || 'membro'}. Posicione o rosto no circulo.`);
      } catch (setupError) {
        const message = setupError.message || 'Falha ao iniciar reconhecimento facial.';
        setFaceError(message);
        setFaceStatus('');
        if (!toast.isActive(FACE_SETUP_ERROR_TOAST_ID)) {
          toast({
            id: FACE_SETUP_ERROR_TOAST_ID,
            title: 'Erro no reconhecimento facial',
            description: message,
            status: 'error',
            duration: 5000,
            isClosable: true
          });
        }
      } finally {
        setIsFaceSetupLoading(false);
        faceInitRef.current = false;
      }
    })();

    try {
      await faceSetupPromiseRef.current;
    } finally {
      faceSetupPromiseRef.current = null;
    }
  }, [ensureFaceModelsLoaded, faceTargetMember, getMemberDescriptor, startCamera, toast]);

  const runFaceRecognition = useCallback(async ({ auto = false } = {}) => {
    if (auto && Date.now() < autoScanCooldownUntilRef.current) {
      return false;
    }

    if (isFaceSetupLoading || faceMatchingRef.current) {
      return false;
    }

    if (!faceTargetMember) {
      setFaceStatus('Informe seu CIM para iniciar o reconhecimento facial.');
      setFaceError('');
      return false;
    }

    if (!videoRef.current || videoRef.current.readyState < 2) {
      if (!auto) {
        setFaceError('Camera ainda nao esta pronta. Aguarde alguns segundos.');
      }
      setFaceStatus('Encaixe seu rosto dentro do circulo para iniciar a leitura.');
      return false;
    }

    faceMatchingRef.current = true;
    if (!auto) {
      setFaceError('');
      setFaceStatus('Capturando rosto na camera...');
      setIsFaceMatching(true);
    }

    try {
      const liveDetection = await faceapi
        .detectSingleFace(videoRef.current, buildFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (!liveDetection) {
        setFaceError('');
        setFaceStatus('Encaixe seu rosto dentro do circulo para iniciar a leitura.');
        return false;
      }

      const targetDescriptor = await getMemberDescriptor(faceTargetMember);
      if (!targetDescriptor) {
        throw new Error('Nao foi possivel carregar a foto do CIM informado.');
      }

      setFaceError('');
      setFaceStatus(`Rosto detectado. Comparando com ${faceTargetMember?.nome || 'membro'}...`);
      const distance = faceapi.euclideanDistance(liveDetection.descriptor, targetDescriptor);
      if (!Number.isFinite(distance) || distance > FACE_MATCH_THRESHOLD) {
        const distanceText = Number.isFinite(distance) ? distance.toFixed(3) : 'n/d';
        setFaceError('Rosto nao confere com o CIM informado.');
        setFaceStatus(`Comparacao sem confianca suficiente (distancia ${distanceText}). Aproxime e melhore a luz.`);
        autoScanCooldownUntilRef.current = Date.now() + 1200;
        return false;
      }

      setFaceError('');
      setFaceStatus(`Rosto reconhecido (${distance.toFixed(3)}). Entrando...`);
      const userData = await performFacialLogin(faceTargetMember);
      if (!userData) {
        throw new Error('Reconhecimento facial ok, mas login nao autorizado.');
      }

      firstAccessModal.onClose();
      passwordModal.onClose();
      faceModal.onClose();
      finishLoginWithUser(userData);
      toast({
        title: 'Login facial confirmado',
        description: `Bem-vindo, ${userData.nome || 'membro'}.`,
        status: 'success',
        duration: 4000,
        isClosable: true
      });
      return true;
    } catch (matchingError) {
      const message = matchingError.message || 'Falha ao reconhecer rosto.';
      setFaceError(message);
      autoScanCooldownUntilRef.current = Date.now() + 4000;
      if (!auto) {
        setFaceStatus('');
      }
      if (!auto && !toast.isActive(FACE_MATCH_ERROR_TOAST_ID)) {
        toast({
          id: FACE_MATCH_ERROR_TOAST_ID,
          title: 'Falha no reconhecimento facial',
          description: message,
          status: 'warning',
          duration: 5000,
          isClosable: true
        });
      }
      return false;
    } finally {
      faceMatchingRef.current = false;
      if (!auto) {
        setIsFaceMatching(false);
      }
    }
  }, [
    buildFaceDetectorOptions,
    finishLoginWithUser,
    faceTargetMember,
    firstAccessModal,
    getMemberDescriptor,
    isFaceSetupLoading,
    passwordModal,
    performFacialLogin,
    faceModal,
    toast
  ]);

  useEffect(() => {
    initializeFaceAuthRef.current = initializeFaceAuth;
  }, [initializeFaceAuth]);

  useEffect(() => {
    if (!faceModal.isOpen || !faceTargetMember || !isCameraReady || isFaceSetupLoading) {
      if (autoScanIntervalRef.current) {
        clearInterval(autoScanIntervalRef.current);
        autoScanIntervalRef.current = null;
      }
      return undefined;
    }

    setFaceStatus((previous) => previous || 'Encaixe seu rosto dentro do circulo para iniciar a leitura.');

    if (!autoScanIntervalRef.current) {
      const tick = () => {
        runFaceRecognition({ auto: true });
      };

      tick();
      autoScanIntervalRef.current = setInterval(tick, FACE_AUTO_SCAN_INTERVAL_MS);
    }

    return () => {
      if (autoScanIntervalRef.current) {
        clearInterval(autoScanIntervalRef.current);
        autoScanIntervalRef.current = null;
      }
    };
  }, [faceModal.isOpen, faceTargetMember, isCameraReady, isFaceSetupLoading, runFaceRecognition]);

  useEffect(() => {
    if (!faceModal.isOpen) {
      stopCamera();
      faceInitRef.current = false;
      faceSetupPromiseRef.current = null;
      resetFaceRuntimeState();
      setFaceError('');
      setFaceStatus('');
      setIsFaceSetupLoading(false);
      setIsFaceMatching(false);
      setFacePin('');
      setFacePinLoading(false);
      setFaceTargetMember(null);
      return;
    }

    if (!faceTargetMember) {
      setFaceStatus('Informe seu CIM para iniciar o reconhecimento facial.');
      setFaceError('');
      return;
    }

    let cancelled = false;
    const runInitialization = async () => {
      if (initializeFaceAuthRef.current) {
        await initializeFaceAuthRef.current(faceTargetMember);
      }
      if (cancelled) {
        stopCamera();
      }
    };
    runInitialization();

    return () => {
      cancelled = true;
      stopCamera();
      faceInitRef.current = false;
      faceSetupPromiseRef.current = null;
      resetFaceRuntimeState();
    };
  }, [faceModal.isOpen, faceTargetMember, resetFaceRuntimeState, stopCamera]);

  if (showSplash) {
    return <SplashScreen />;
  }

  const isAuthBusy =
    isLoading ||
    passwordLoading ||
    facePinLoading ||
    isFaceSetupLoading ||
    isFaceMatching;

  const authBusyLabel = (() => {
    if (isFaceMatching) {
      return faceStatus || 'Processando reconhecimento facial...';
    }
    if (isFaceSetupLoading) {
      return faceStatus || 'Inicializando camera...';
    }
    if (facePinLoading) {
      return 'Validando CIM...';
    }
    if (passwordLoading) {
      return 'Entrando com sua senha...';
    }
    if (isLoading) {
      return 'Validando seus dados...';
    }
    return 'Processando...';
  })();

  return (
    <Box minH="100vh" position="relative" overflow="hidden">
      <Box
        position="absolute"
        inset="0"
        backgroundImage="url('/fundo.jpeg')"
        backgroundSize="cover"
        backgroundPosition="52% 18%"
      />
      <Box
        position="absolute"
        inset="0"
        bg="linear-gradient(180deg, rgba(20,50,100,0.38), rgba(10,26,36,0.62))"
      />

      {isAuthBusy ? (
        <Portal>
          <Flex
            position="fixed"
            inset={0}
            zIndex={5000}
            align="center"
            justify="center"
            bg="rgba(8, 18, 32, 0.68)"
            backdropFilter="blur(8px)"
            px={6}
          >
            <VStack
              spacing={4}
              bg="rgba(255, 255, 255, 0.98)"
              color="#0b1725"
              borderRadius="24px"
              px={{ base: 6, md: 8 }}
              py={{ base: 6, md: 7 }}
              boxShadow="0 22px 60px rgba(0, 0, 0, 0.34)"
              textAlign="center"
              maxW="360px"
              width="100%"
            >
              <Spinner size="xl" color="#1F4B99" thickness="4px" speed="0.75s" />
              <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="700">
                Aguarde um instante
              </Text>
              <Text fontSize="sm" color="gray.600">
                {authBusyLabel}
              </Text>
            </VStack>
          </Flex>
        </Portal>
      ) : null}

      <Flex
        position="relative"
        zIndex={1}
        minH="100vh"
        direction="column"
        align={{ base: 'stretch', md: 'center' }}
        justify={{ base: 'space-between', md: 'space-between' }}
        px={{ base: 4, sm: 6, md: 10 }}
        py={{ base: 8, md: 10 }}
      >
        <VStack spacing={1} align="center" mt={{ base: 4, md: 8 }}>
          {['LOJA MACONICA', 'ESTRELA CALDENSE'].map((line) => (
            <Text
              key={line}
              fontSize={{ base: '24px', md: '28px' }}
              fontFamily="Marcellus"
              textTransform="uppercase"
              letterSpacing="0.18em"
              color="white"
              textShadow="0 3px 14px rgba(0,0,0,0.65)"
            >
              {line}
            </Text>
          ))}
        </VStack>

        <Box flex="1" />

        <Box width="100%" maxW={{ base: '100%', sm: '460px', md: '480px' }} mb={{ base: 6, md: 0 }}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <FormControl id="cim" isRequired>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaUser color="#1F4B99" size="18" />
                  </InputLeftElement>
                  <Input
                    type="text"
                    value={cim}
                    onChange={handleCimChange}
                    placeholder="Digite seu CIM"
                    maxLength="6"
                    {...sharedInputStyles}
                  />
                </InputGroup>
              </FormControl>

              <FormControl id="cpf" isRequired>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaKey color="#1F4B99" size="18" />
                  </InputLeftElement>
                  <Input
                    type="text"
                    value={cpf}
                    onChange={handleCpfChange}
                    placeholder="Digite seu CPF"
                    {...sharedInputStyles}
                  />
                </InputGroup>
              </FormControl>

              {error && (
                <Text color="red.500" fontSize="sm" textAlign="center">
                  {error}
                </Text>
              )}

              <Button
                type="submit"
                bg="#1F4B99"
                color="white"
                size="lg"
                width="full"
                borderRadius="18px"
                _hover={{ bg: '#163c7a' }}
                isLoading={isLoading}
                loadingText="Validando..."
                leftIcon={!isLoading ? <FaKey /> : undefined}
              >
                {isLoading ? 'Validando...' : 'ENTRAR'}
              </Button>

              <Button
                type="button"
                variant="outline"
                borderColor="white"
                color="white"
                borderRadius="18px"
                size="lg"
                width="full"
                leftIcon={<FaUserCircle />}
                _hover={{ bg: 'rgba(255,255,255,0.15)' }}
                onClick={handleOpenFaceModal}
              >
                ENTRAR COM FACIAL
              </Button>
            </VStack>
          </form>
        </Box>
      </Flex>

      <Modal isOpen={firstAccessModal.isOpen} onClose={firstAccessModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="24px" p={2}>
          <ModalHeader>Primeiro acesso</ModalHeader>
          <ModalBody>
            <Text color="gray.600">
              Ir {pendingUser?.nome ? `${pendingUser.nome}, ` : ''}
              identificamos que este e seu primeiro acesso. Crie sua senha para continuar.
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={firstAccessModal.onClose}>
              Voltar
            </Button>
            <Button
              bg="#1F4B99"
              color="white"
              _hover={{ bg: '#163c7a' }}
              onClick={() => {
                firstAccessModal.onClose();
                navigate('/criar-senha');
              }}
            >
              Criar senha
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={passwordModal.isOpen} onClose={passwordModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="24px" p={2}>
          <ModalHeader color="gray.600">Digite sua senha</ModalHeader>
          <ModalBody>
            <FormControl isInvalid={Boolean(passwordError)}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FaLock color="#1F4B99" size="18" />
                </InputLeftElement>

                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha"
                  {...sharedInputStyles}
                />
              </InputGroup>
              {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
            </FormControl>
            <Text color="gray.500" fontSize="xs" mt={3}>
              Tentativas: {attempts} / 3
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={passwordModal.onClose}>
              Cancelar
            </Button>
            <Button
              bg="#1F4B99"
              color="white"
              _hover={{ bg: '#163c7a' }}
              onClick={handlePasswordSubmit}
              isLoading={passwordLoading}
              loadingText="Entrando..."
            >
              Entrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={faceModal.isOpen} onClose={faceModal.onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="20px" p={2}>
          <ModalHeader>Login por Reconhecimento Facial</ModalHeader>
          <ModalBody>
            <Text mb={3} fontSize="sm" color="gray.600">
              Digite seu CIM. Com o CIM validado, o sistema compara seu rosto apenas com sua foto cadastrada.
            </Text>

            <HStack spacing={2} mb={3}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FaKey color="#1F4B99" size="16" />
                </InputLeftElement>
                <Input
                  value={facePin}
                  onChange={(event) => setFacePin(extractDigits(event.target.value))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleFacePinSubmit();
                    }
                  }}
                  placeholder="Digite seu CIM"
                  maxLength={12}
                  {...sharedInputStyles}
                />
              </InputGroup>
              <Button
                bg="#1F4B99"
                color="white"
                _hover={{ bg: '#163c7a' }}
                onClick={handleFacePinSubmit}
                isLoading={facePinLoading}
                loadingText="Validando..."
                minW="132px"
              >
                Validar CIM
              </Button>
            </HStack>

            {faceTargetMember ? (
              <Text mb={3} fontSize="sm" color="green.600">
                CIM validado para {faceTargetMember.nome || 'membro'}.
              </Text>
            ) : null}

            <Box className="igm-face-scanner-wrapper">
              <video ref={videoRef} className="igm-face-scanner-video" autoPlay muted playsInline />
              <div className="igm-face-scanner-mask" />
              <div className="igm-face-scanner-line" />
              {!faceTargetMember ? (
                <Flex
                  position="absolute"
                  inset={0}
                  align="center"
                  justify="center"
                  bg="rgba(0, 0, 0, 0.55)"
                  color="white"
                  px={4}
                  textAlign="center"
                  fontSize="sm"
                >
                  Valide o CIM para liberar a leitura facial.
                </Flex>
              ) : null}
            </Box>

            {(isFaceSetupLoading || isFaceMatching) ? (
              <HStack mt={3} spacing={3}>
                <Spinner size="sm" />
                <Text fontSize="sm">
                  {isFaceMatching ? 'Processando reconhecimento...' : 'Inicializando camera...'}
                </Text>
              </HStack>
            ) : null}

            {faceStatus ? (
              <Text mt={3} fontSize="sm" color="gray.700">
                {faceStatus}
              </Text>
            ) : null}

            {faceError ? (
              <Text mt={2} fontSize="sm" color="red.500">
                {faceError}
              </Text>
            ) : null}
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={faceModal.onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Login;
